import { useState } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { resetPasswordSchema } from '../../lib/validators/authSchemas';
import { useResetPasswordMutation, useForgotPasswordMutation } from '../../features/auth/authApi';
import { useResendCooldown } from '../../lib/useResendCooldown';
import { authErrorMessage } from '../../lib/authErrorMessage';
import OtpInput from '../../components/auth/OtpInput';
import PasswordInput from '../../components/ui/PasswordInput';
import Button from '../../components/ui/Button';

/**
 * The real API resets a password in a single call — { email, otp, password }
 * — there's no separate "verify the code first" step, so this one screen
 * collects the code and the new password together rather than splitting
 * across two pages.
 */
export default function ResetPassword() {
  const location = useLocation();
  const navigate = useNavigate();
  const [resetPassword, { isLoading }] = useResetPasswordMutation();
  const [resendCode, { isLoading: resending }] = useForgotPasswordMutation();
  const { remaining, start, canResend } = useResendCooldown(60);
  const [resendMessage, setResendMessage] = useState('');

  const email = location.state?.email;

  const {
    register,
    handleSubmit,
    control,
    setError,
    formState: { errors },
  } = useForm({
    resolver: zodResolver(resetPasswordSchema),
    defaultValues: { email: email ?? '', otp: '', password: '', password_confirmation: '' },
  });

  if (!email) {
    return (
      <div className="text-center">
        <p className="text-sm text-foreground-muted mb-4">
          Start a password reset first so we know which account to reset.
        </p>
        <Link to="/forgot-password"><Button className="w-full">Request a reset code</Button></Link>
      </div>
    );
  }

  const onSubmit = async ({ password_confirmation, ...values }) => {
    try {
      await resetPassword(values).unwrap();
      navigate('/login', { replace: true, state: { passwordReset: true } });
    } catch (err) {
      setError('root', { message: authErrorMessage(err, { fallback: 'Invalid or expired code. Request a new one below.' }) });
    }
  };

  const onResend = async () => {
    setResendMessage('');
    try {
      await resendCode({ email }).unwrap();
      setResendMessage('A new code is on its way.');
      start();
    } catch (err) {
      setResendMessage(authErrorMessage(err, { fallback: 'Could not resend code — try again shortly.' }));
    }
  };

  return (
    <div>
      <h1 className="text-xl font-semibold text-foreground mb-1">Set a new password</h1>
      <p className="text-sm text-foreground-muted mb-6">
        Enter the code we sent to <span className="text-foreground">{email}</span> and choose a new password.
      </p>

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-5" noValidate>
        <input type="hidden" {...register('email')} />

        <Controller
          name="otp"
          control={control}
          render={({ field }) => <OtpInput value={field.value} onChange={field.onChange} error={errors.otp?.message} />}
        />

        <PasswordInput
          label="New password"
          autoComplete="new-password"
          error={errors.password?.message}
          {...register('password')}
        />
        <PasswordInput
          label="Confirm new password"
          autoComplete="new-password"
          error={errors.password_confirmation?.message}
          {...register('password_confirmation')}
        />

        {errors.root && <p className="text-sm text-danger">{errors.root.message}</p>}

        <Button type="submit" className="w-full" isLoading={isLoading}>
          Reset password
        </Button>
      </form>

      <p className="text-sm text-foreground-muted mt-6 text-center">
        Didn't get a code?{' '}
        <button
          type="button"
          onClick={onResend}
          disabled={!canResend || resending}
          className="text-accent hover:underline disabled:text-foreground-subtle disabled:no-underline disabled:cursor-not-allowed"
        >
          {canResend ? 'Resend code' : `Resend in ${remaining}s`}
        </button>
      </p>
      {resendMessage && <p className="text-xs text-foreground-subtle mt-2 text-center">{resendMessage}</p>}

      <p className="text-sm text-foreground-muted mt-4 text-center">
        <Link to="/login" className="text-accent hover:underline">
          Back to login
        </Link>
      </p>
    </div>
  );
}
